/**
 * Brand tokens, in a form Chart.js can eat.
 *
 * Chart.js paints to a canvas, so it cannot use a Tailwind class and cannot
 * resolve a CSS variable - it wants a literal colour string. The economics
 * charts used to carry their own hex palette (`#6366f1`, `#22c55e`, …), which
 * is exactly the "no raw hex where a token exists" rule, and it did not flip
 * with dark mode: an indigo bar sat on ink and glared.
 *
 * So the palette is READ from the same `--chart-*` / `--champagne` variables
 * `index.css` defines, at render time, and re-read whenever the theme class on
 * <html> changes. One source of truth, two themes, no hex in a component.
 */

import { useEffect, useState } from 'react';

export interface ChartTokens {
  /** The five-step analytics palette (`--chart-1` … `--chart-5`). */
  palette: string[];
  /** THE accent - budget consumption fills. */
  champagne: string;
  /** The accent at WIP weight (unbilled work sits behind billed work). */
  champagneSoft: string;
  /** Axis labels, legends, gridlines. */
  muted: string;
  grid: string;
  /** Neutral track behind a consumed bar. */
  track: string;
  danger: string;
}

/** `--chart-1: 91 107 148` → `rgb(91 107 148)`. */
function readVar(styles: CSSStyleDeclaration, name: string, alpha?: number): string {
  const raw = styles.getPropertyValue(name).trim();
  if (!raw) return alpha != null ? `rgb(0 0 0 / ${alpha})` : 'rgb(0 0 0)';
  return alpha != null ? `rgb(${raw} / ${alpha})` : `rgb(${raw})`;
}

function readTokens(): ChartTokens {
  // SSR / jsdom without a real computed style still needs a shape back.
  if (typeof window === 'undefined' || !document?.documentElement) {
    return {
      palette: [], champagne: 'rgb(0 0 0)', champagneSoft: 'rgb(0 0 0 / 0.4)',
      muted: 'rgb(0 0 0)', grid: 'rgb(0 0 0 / 0.1)', track: 'rgb(0 0 0 / 0.06)',
      danger: 'rgb(0 0 0)',
    };
  }
  const styles = getComputedStyle(document.documentElement);
  return {
    palette: [1, 2, 3, 4, 5].map((i) => readVar(styles, `--chart-${i}`)),
    champagne: readVar(styles, '--champagne'),
    champagneSoft: readVar(styles, '--champagne', 0.4),
    muted: readVar(styles, '--fg-muted'),
    grid: readVar(styles, '--hairline'),
    track: readVar(styles, '--surface-sunken'),
    danger: readVar(styles, '--danger'),
  };
}

/**
 * The current theme's chart palette. Re-reads on a light/dark flip, because
 * `applyTheme` swaps the `light`/`dark` class on <html> and every variable
 * above changes value underneath us.
 */
export function useChartTokens(): ChartTokens {
  const [tokens, setTokens] = useState<ChartTokens>(readTokens);

  useEffect(() => {
    const root = document?.documentElement;
    if (!root || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => setTokens(readTokens()));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return tokens;
}
